import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { AsyncPipe, NgClass } from '@angular/common';
import { distinctUntilChanged, startWith, takeUntil } from 'rxjs';

import { ILabelDescriptionView, IOfficeField, LabelTypeEnum } from '@pages/office/models/office-data.model';
import { LookupButtonComponent } from '../lookup-button/lookup-button.component';
import { FormControlErrorsComponent } from '../control-errors/control-errors.component';
import { ControlErrorsMessagesOverridesRegistryModel } from '../control-errors/models/control-custom-errors.model';
import { OfficeFormHelperService } from '../../office-form-helper.service';
import { AsyncStatusFromLabelValuePipe } from '../pipes/status-from-label-value.pipe';
import { ControlLabelAlignment } from '@pages/office/SingleGridForm/office-form/form-controls/control-wrapper/models/control-wrapper.models';
import { FormControlConfig } from '../models/form-control-config.model';
import { isEmptyValue } from '@core/helpers/value-empty-checker.helper';
import { DestroyableDirective } from '@shared/directive/destroyable.directive';

@Component({
  selector: 'app-control-wrapper',
  templateUrl: 'control-wrapper.component.html',
  styleUrls: ['control-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [LookupButtonComponent, FormControlErrorsComponent, NgClass, AsyncStatusFromLabelValuePipe, AsyncPipe],
})
export class ControlWrapperComponent extends DestroyableDirective implements OnInit {
  @Input({ required: true }) control: AbstractControl;
  @Input({ required: true }) officeField: IOfficeField;
  @Input({ required: true }) formGroupInstance: FormGroup;
  @Input() errorsMessagesOverridesRegistry: ControlErrorsMessagesOverridesRegistryModel;
  @Input() disabled: boolean;
  @Input() required: boolean;
  @Input() validating: boolean;
  @Input() alignment: ControlLabelAlignment;
  @Input() config: FormControlConfig;

  public labelLocalization$ = signal<string>(null);
  public labelDescriptionViews: ILabelDescriptionView[];

  readonly LabelTypeEnum = LabelTypeEnum;
  readonly formHelperService = inject(OfficeFormHelperService);
  readonly controlLabelAlignment = ControlLabelAlignment;

  ngOnInit(): void {
    this.officeField.FieldLabelLocalization && this.getLabelLocalization();
    !this.config?.hideLabelStatus && this.initializeLabelDescriptionViews();
  }

  private initializeLabelDescriptionViews(): void {
    const labelDescriptions = this.officeField.LabelDescriptions;
    if (!isEmptyValue(labelDescriptions)) {
      this.labelDescriptionViews = labelDescriptions
        .filter(labelDescriptions => !!this.formHelperService.getControl(String(labelDescriptions.Seq)))
        .map(labelDesc => {
          const control = this.formHelperService.getControl(labelDesc.Seq);
          return { seq: labelDesc.Seq, control, type: labelDesc.Type };
        });
    }
  }

  private getLabelLocalization(): void {
    const { FieldLabelLocalization } = this.officeField;
    const control = this.formHelperService.getControl(FieldLabelLocalization.Seq);
    control.valueChanges
      .pipe(takeUntil(this.destroy$), startWith(control.value), distinctUntilChanged())
      .subscribe(value => this.labelLocalization$.set(FieldLabelLocalization.Localizations[value]));
  }
}
